// src/App.js
import React, { useEffect, useState } from "react";
import { PrimeReactProvider } from "primereact/api";
import { AppRouter } from "./router/AppRouter";
import { useChartsStore } from "./store/chartStore";
import useWorkspaceStore from "./store/workspaceStore";
import { useAuthStore } from "./store/authStore";
import useRatingStore from "./store/ratingStore";
import marketdata from "./data/Services/marketdata";
// TODO: Re-enable when price crossing detection is fixed
// import { initStopLineAlertService, destroyStopLineAlertService } from './data/Services/stopLineAlertService';
import logger from "./utils/logger";
import { startClientSnapshot } from "./utils/clientSnapshot";
import { startOrdersSnapshot } from "./utils/ordersSnapshot";
import { persistActiveDesktop } from "./workspace/desktopManager";
import { usePageVisibilityReconnect } from "./hooks/usePageVisibilityReconnect";
import { useWakeLock } from "./hooks/useWakeLock";
import EvictedScreen from "./components/EvictedScreen";
import OfflineOverlay from "./components/OfflineOverlay";
import DebugOverlay from "./components/DebugOverlay/DebugOverlay";
import NewsNotifications from "./components/NewsNotifications/NewsNotifications";
import { RatingModal } from "./components/Modals";
import QuantityModalProvider from "./components/Modals/QuantityModalProvider";
import { ToastProvider } from "./components/ToastProvider";
import { ConfirmDialog } from "primereact/confirmdialog";
import { APP_DISPLAY_NAME } from "./config/appConfig";
import { WhatsNewPanel, useWhatsNew } from "./components/WhatsNew";
import TipOfTheDay from "./components/TipOfTheDay/TipOfTheDay";
// import { AuthProvider } from './context/AuthContext';
// import { ChartsProvider } from './context/ChartsContext';
import "./utils/devTools"; // Dev tools for cache management (available in console)
import "./App.css";

function AppMain() {
  // Chart state is read via useChartsStore.getState() (auto-save, below), never a
  // subscription — App must not re-render on chart/quote changes.
  const desktopName = useWorkspaceStore((state) => state.desktopName);
  const getUsername = useAuthStore((state) => state.getUsername);
  const getBroker = useAuthStore((state) => state.getBroker);

  // Show last auto-save attempt at startup (survives console clear)
  useEffect(() => {
    const lastAttempt = sessionStorage.getItem("debug-last-autosave");
    if (lastAttempt) {
      console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
      console.log("🔍 LAST AUTO-SAVE ATTEMPT (from previous session):");
      console.log(lastAttempt);
      console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
    }
  }, []);

  // Clear stale skip-autosave flag from previous factory reset
  useEffect(() => {
    const skipFlag = sessionStorage.getItem("skip-autosave");
    if (skipFlag === "true") {
      console.log(
        "[App] Clearing stale skip-autosave flag from previous session",
      );
      sessionStorage.removeItem("skip-autosave");
    }
  }, []);

  useEffect(() => {
    startClientSnapshot();
    startOrdersSnapshot();
  }, []);

  // TODO: Re-enable when price crossing detection is fixed
  // Initialize StopLine alert service
  // useEffect(() => {
  //     initStopLineAlertService();
  //     return () => {
  //         destroyStopLineAlertService();
  //     };
  // }, []);

  // Rating modal state
  const isRatingVisible = useRatingStore((state) => state.isVisible);
  const hideRatingModal = useRatingStore((state) => state.hideModal);
  const submitRating = useRatingStore((state) => state.submitRating);

  // What's New panel state
  const {
    isOpen: isWhatsNewOpen,
    currentVersion,
    close: closeWhatsNew,
  } = useWhatsNew();

  // Handle automatic reconnection when page becomes visible after being hidden (mobile background tabs)
  usePageVisibilityReconnect();

  // Prevent screen from dimming/locking (configurable in settings)
  useWakeLock();

  // Update browser tab title when desktop name changes
  useEffect(() => {
    document.title = `${desktopName} - ${APP_DISPLAY_NAME}`;
  }, [desktopName]);

  // Detect page closing/unloading to save workspace and log disconnection
  useEffect(() => {
    const handleBeforeUnload = () => {
      console.log("[BeforeUnload] Event fired");

      // Check if auto-save should be skipped (e.g., during factory reset)
      if (sessionStorage.getItem("skip-autosave") === "true") {
        console.log(
          "[BeforeUnload] Auto-save skipped (factory reset in progress)",
        );
        return;
      }

      // Auto-save current workspace before browser closes (synchronous)
      try {
        const user = getUsername();
        const broker = getBroker();

        // Get FRESH state from store (not closure) to avoid stale data
        const freshState = useChartsStore.getState();

        const debugInfo = [
          `Timestamp: ${new Date().toLocaleString()}`,
          `User: ${user}`,
          `Broker: ${broker}`,
          `Charts: ${freshState?.charts?.length || 0}`,
          `Symbols: ${freshState?.charts?.map((c) => c.symbol).join(", ") || "none"}`,
        ];

        console.log("[BeforeUnload] User:", user, "Broker:", broker);
        console.log(
          "[BeforeUnload] Charts from fresh state:",
          freshState?.charts?.length || 0,
        );
        console.log(
          "[BeforeUnload] Charts symbols:",
          freshState?.charts?.map((c) => c.symbol) || [],
        );

        const result = persistActiveDesktop(freshState, user, broker);
        console.log("[BeforeUnload] Auto-save result:", result);

        if (result) {
          debugInfo.push("Result: ✅ SUCCESS");
          console.log(
            "[BeforeUnload] ✅ Workspace auto-saved before browser close",
          );
        } else {
          debugInfo.push("Result: ❌ FAILED (no charts)");
          console.log(
            "[BeforeUnload] ❌ Auto-save returned false (probably no charts)",
          );
        }

        // Save debug info to sessionStorage (survives console clear)
        sessionStorage.setItem("debug-last-autosave", debugInfo.join("\n"));
      } catch (error) {
        console.error(
          "[BeforeUnload] ❌ Failed to auto-save workspace:",
          error,
        );
        sessionStorage.setItem(
          "debug-last-autosave",
          `ERROR: ${error.message}`,
        );
      }

      // Preserve authentication for page reload (F5, Ctrl+R, etc.)
      // This allows users to refresh without losing their login session
      try {
        const authStore = useAuthStore.getState();
        const broker = marketdata.broker;
        const auth = marketdata.auth;
        const loginMethod = authStore.isAuthenticatedViaBrokerAuth()
          ? "brokerAuth"
          : "credentials";

        if (broker && auth) {
          const authData = {
            broker,
            auth,
            loginMethod,
            timestamp: Date.now(),
          };

          localStorage.setItem("force-reload-auth", JSON.stringify(authData));
          console.log("[BeforeUnload] Auth data saved for page reload");

          // For brokerAuth users, preserve original token with suffix
          if (loginMethod === "brokerAuth") {
            const originalToken = sessionStorage.getItem("original-auth-token");
            if (originalToken) {
              localStorage.setItem(
                "force-reload-original-token",
                originalToken,
              );
              console.log("[BeforeUnload] Original auth token preserved");
            }
          }
        }
      } catch (error) {
        console.error("[BeforeUnload] Failed to preserve auth data:", error);
      }

      // Log disconnection when browser is closing
      if (marketdata.state === "online") {
        logger.connection.info(
          "Fechando navegador, desconectado do servidor de dados",
        );
      }
    };

    console.log("[App] Registering beforeunload event listener (once)");

    // Add event listener for page unload
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup event listener on component unmount
    return () => {
      console.log("[App] Removing beforeunload event listener on unmount");
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []); // Empty deps - register ONCE, get fresh state on every call

  // Periodic auto-save every 5 minutes when user is active
  useEffect(() => {
    let autoSaveInterval;
    let lastActivity = Date.now();

    const updateActivity = () => {
      lastActivity = Date.now();
    };

    const periodicAutoSave = () => {
      // Skip if factory reset is in progress
      if (sessionStorage.getItem("skip-autosave") === "true") {
        return;
      }

      // Get FRESH state from store (not closure)
      const freshState = useChartsStore.getState();

      // Only auto-save if user was active in the last 10 minutes
      const timeSinceActivity = Date.now() - lastActivity;
      const tenMinutes = 10 * 60 * 1000;

      if (timeSinceActivity < tenMinutes && freshState?.charts?.length > 0) {
        try {
          const user = getUsername();
          const broker = getBroker();
          persistActiveDesktop(freshState, user, broker);
          console.log("Periodic workspace auto-save completed");
        } catch (error) {
          console.error("Failed to auto-save workspace periodically:", error);
        }
      }
    };

    // Track user activity
    const activityEvents = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
    ];
    activityEvents.forEach((event) => {
      document.addEventListener(event, updateActivity, true);
    });

    // Set up auto-save interval (every 5 minutes)
    autoSaveInterval = setInterval(periodicAutoSave, 5 * 60 * 1000);

    // Cleanup
    return () => {
      if (autoSaveInterval) {
        clearInterval(autoSaveInterval);
      }
      activityEvents.forEach((event) => {
        document.removeEventListener(event, updateActivity, true);
      });
    };
  }, []); // Empty deps - uses getState() inside callback for fresh state

  return (
    <PrimeReactProvider
      value={{
        hideOverlaysOnDocumentScrolling: true,
        ripple: false,
        // Disable dynamic CSS injection to prevent STYLE tag creation during animation
        cssTransition: false,
        // Append overlays to dedicated container to prevent layout thrashing
        appendTo: "self",
      }}
    >
      <ToastProvider>
        <div className="App">
          <OfflineOverlay />
          <DebugOverlay />
          <NewsNotifications />
          <WhatsNewPanel
            isOpen={isWhatsNewOpen}
            version={currentVersion}
            onClose={closeWhatsNew}
          />
          <RatingModal
            visible={isRatingVisible}
            onHide={hideRatingModal}
            onSubmit={submitRating}
          />
          <QuantityModalProvider />
          <div className="content">
            <AppRouter />
          </div>
          <ConfirmDialog />
          <TipOfTheDay />
        </div>
      </ToastProvider>
    </PrimeReactProvider>
  );
}

/**
 * #304: dead-end gate. Renders <EvictedScreen /> in place of the entire app
 * when this tab was evicted by the server. Sticky via sessionStorage — F5 stays
 * on the dead-end; only closing the tab and opening a new one resets it.
 */
function App() {
  const [evicted, setEvicted] = useState(
    () =>
      marketdata.closedByServer === true ||
      (typeof sessionStorage !== "undefined" &&
        sessionStorage.getItem("marketdata_evicted") === "1"),
  );
  useEffect(() => {
    if (evicted) return undefined;
    const id = setInterval(() => {
      if (marketdata.closedByServer === true) setEvicted(true);
    }, 500);
    return () => clearInterval(id);
  }, [evicted]);
  if (evicted) {
    return <EvictedScreen />;
  }
  return <AppMain />;
}

export default App;
